// What the user did with a search — the only measure of search quality once
// there is no reference engine to compare against.
//
// Every settled content search opens a session (see SearchProviderContainer),
// which is also when its "Minisearch Results Report" is sent. Each user action
// on that search then emits a "Minisearch Results Outcome" event carrying the
// same `searchId`, so the two join exactly instead of on query text in a time
// window.
//
// On a click the outcome records where the item sat in the displayed list AND
// where every shadow profile ranked it. Across many clicks that says whether a
// candidate would have put the item users actually chose higher or lower than
// production — a paired comparison that needs no A/B split.
//
// A session that ends with no action is a signal too: `refined` when the user
// typed a different query, `abandoned` when they cleared or left the search.
//
// Sessions are kept per surface at module level rather than in React context:
// TopNavSearchBar renders its orchestrator inside itself, so its own click
// handlers sit outside the SearchContext they would need. Each surface has at
// most one live search at a time.

import { v4 as uuidv4 } from 'uuid'
import { getDocumentType } from '../../utils/search/getDocumentType'
import { searchDebugLog } from './searchDebugLog'
import analytics from '../../utils/cdpAnalytics'

export const RESULTS_OUTCOME_EVENT = 'Minisearch Results Outcome'

// Mirrors the report's positional shadow columns.
const SHADOW_COLUMN_SLOTS = 2

const INDEX_BY_DOCUMENT_TYPE = {
  repo: 'repos',
  folder: 'folders',
  set: 'sets_questions',
  question: 'sets_questions',
}

const sessions = {}
const flushers = {}

// 1-based rank of every id, per index.
function rankMaps(indexResults) {
  const out = {}
  Object.keys(indexResults || {}).forEach((indexName) => {
    const slot = indexResults[indexName] || {}
    const ids = Array.isArray(slot.rankedIds) ?
      slot.rankedIds :
      (slot.hits || []).map((h) => h.objectID)
    out[indexName] = new Map(ids.map((id, i) => [id, i + 1]))
  })
  return out
}

function rankIn(maps, indexName, objectId) {
  const map = maps && maps[indexName]
  if (!map || objectId == null) return null
  return map.get(objectId) || null
}

function emit(props) {
  analytics.track(RESULTS_OUTCOME_EVENT, props)
  searchDebugLog(`[searchSession] ${RESULTS_OUTCOME_EVENT}`, props)
}

function baseProps(session, action) {
  const props = {
    searchId: session.searchId,
    query: session.query,
    surface: session.surface,
    action,
    displayProfile: session.displayProfile,
    msSinceResults: Date.now() - session.openedAt,
    kind: null,
    objectId: null,
    itemType: null,
    index: null,
    rank: null,
    nextQueryExtends: null,
  }
  for (let slot = 0; slot < SHADOW_COLUMN_SLOTS; slot += 1) {
    props[`shadow${slot + 1}Profile`] = session.shadowProfiles[slot] || null
    props[`shadow${slot + 1}Rank`] = null
  }
  return props
}

/**
 * Register the surface's orchestrator so an action taken before its report
 * went out can force it out first. Without this, a query acted on inside the
 * report debounce never gets a report at all.
 *
 * @param {string} surface
 * @param {Function} flush emits the pending report now; must be idempotent
 * @returns {Function} unregister
 */
export function registerSearchSurface(surface, flush) {
  flushers[surface] = flush
  return () => {
    if (flushers[surface] === flush) delete flushers[surface]
  }
}

/**
 * End the surface's current session. If the user did nothing with it, record
 * why it ended: a different query means they refined it, no query means they
 * gave up on it.
 *
 * @param {string} surface
 * @param {{nextQuery?: string}} [options]
 */
export function closeSearch(surface, { nextQuery = '' } = {}) {
  const session = sessions[surface]
  if (!session) return
  delete sessions[surface]
  if (session.engaged) return
  const next = (nextQuery || '').trim()
  if (next) {
    emit({
      ...baseProps(session, 'refined'),
      // A keystroke on the way to a longer query, as opposed to a new one.
      nextQueryExtends: next.toLowerCase().startsWith(session.query.trim().toLowerCase()),
    })
  } else {
    emit(baseProps(session, 'abandoned'))
  }
}

/**
 * Start a session for a settled query and return its id. Replaces (and closes)
 * any session the surface already had for another query.
 *
 * @param {string} surface
 * @param {Object} input
 * @param {string} input.query
 * @param {string} input.displayProfile
 * @param {Object} input.displayIndexResults per-index results with `rankedIds`
 * @param {Array<{profile: string, indexResults: Object}>} input.shadows
 * @returns {string} searchId
 */
export function openSearch(surface, {
  query, displayProfile, displayIndexResults, shadows,
}) {
  const existing = sessions[surface]
  if (existing && existing.query !== query) closeSearch(surface, { nextQuery: query })
  if (existing && existing.query === query) return existing.searchId

  const shadowLanes = Array.isArray(shadows) ? shadows : []
  const session = {
    searchId: uuidv4(),
    surface,
    query,
    displayProfile,
    openedAt: Date.now(),
    engaged: false,
    displayRanks: rankMaps(displayIndexResults),
    shadowProfiles: shadowLanes.map((s) => s.profile),
    shadowRanks: shadowLanes.map((s) => rankMaps(s.indexResults)),
  }
  sessions[surface] = session
  return session.searchId
}

/**
 * Record a user action on the surface's current search.
 *
 * Content actions pass the `hit` acted on, and the outcome carries its rank in
 * the displayed list and in every shadow lane. Actions on the search as a whole
 * (`seeAll`, `submit`, `showMore`) and on non-content rows (`nonContent` with a
 * `kind`) carry no rank.
 *
 * @param {string} surface
 * @param {Object} outcome
 * @param {string} outcome.action   open | openNewTab | preview | play | addToClass |
 *                                  seeAll | submit | showMore | nonContent
 * @param {Object} [outcome.hit]    the content hit acted on
 * @param {string} [outcome.kind]   for nonContent: navigation | quickAction | help
 */
export function recordSearchOutcome(surface, { action, hit, kind } = {}) {
  const flush = flushers[surface]
  if (flush) flush()
  const session = sessions[surface]
  if (!session || !action) return
  session.engaged = true

  const props = { ...baseProps(session, action), kind: kind || null }
  if (hit) {
    const objectId = hit.objectID != null ? hit.objectID : hit.id
    const itemType = getDocumentType(hit)
    const index = INDEX_BY_DOCUMENT_TYPE[itemType] || null
    props.objectId = objectId != null ? String(objectId) : null
    props.itemType = itemType
    props.index = index
    props.rank = rankIn(session.displayRanks, index, objectId)
    session.shadowRanks.slice(0, SHADOW_COLUMN_SLOTS).forEach((maps, slot) => {
      props[`shadow${slot + 1}Rank`] = rankIn(maps, index, objectId)
    })
  }
  emit(props)
}

// Test seam: forget every session and registration.
export function resetSearchSessions() {
  Object.keys(sessions).forEach((key) => delete sessions[key])
  Object.keys(flushers).forEach((key) => delete flushers[key])
}
